import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { defaultUnitPreferences } from '@/utils/units';
import type { Tenant, UnitPreferences } from '@/types';

export function SettingsPage() {
  const { tenantId } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [prefs, setPrefs] = useState<UnitPreferences>(defaultUnitPreferences());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const { data } = await supabase.from('tenants').select('*').eq('id', tenantId).maybeSingle();
      if (data) {
        setTenant(data as unknown as Tenant);
        setPrefs(data.unit_preferences as UnitPreferences);
      }
    })();
  }, [tenantId]);

  const save = async () => {
    if (!tenantId) return;
    setSaving(true);
    await supabase
      .from('tenants')
      .update({ unit_preferences: prefs })
      .eq('id', tenantId);
    setSaving(false);
    setSavedAt(new Date().toLocaleTimeString('ja-JP'));
  };

  if (!tenant) return <div className="p-6 text-sm text-slate-500">読み込み中...</div>;

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">設定</h1>
      </header>

      <section className="space-y-6 p-4">
        {/* テナント情報 */}
        <div>
          <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">アカウント</h2>
          <div className="space-y-1 text-sm">
            <div><span className="text-slate-500">表示名: </span>{tenant.display_name}</div>
            {tenant.company_name && <div><span className="text-slate-500">法人名: </span>{tenant.company_name}</div>}
            <div><span className="text-slate-500">連携先: </span>{tenant.invoicing_provider}</div>
          </div>
        </div>

        {/* 単位設定(原則17) */}
        <div>
          <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">出力単位(原則17)</h2>
          <div className="space-y-3">
            <Field label="クロスの出力単位">
              <select
                value={prefs.wallpaper_output}
                onChange={(e) => setPrefs({ ...prefs, wallpaper_output: e.target.value as 'sqm' | 'm' })}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="sqm">㎡(関東標準)</option>
                <option value="m">m(関西式)</option>
              </select>
            </Field>
            <Field label="床材の出力単位">
              <select
                value={prefs.flooring_output}
                onChange={(e) => setPrefs({ ...prefs, flooring_output: e.target.value as 'sqm' | 'm' })}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="sqm">㎡</option>
                <option value="m">m(長尺)</option>
              </select>
            </Field>
            <Field label="長尺の出力単位">
              <select
                value={prefs.length_output}
                onChange={(e) => setPrefs({ ...prefs, length_output: e.target.value as 'm' | 'cm' | 'mm' })}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="m">m</option>
                <option value="cm">cm</option>
                <option value="mm">mm</option>
              </select>
            </Field>
            <Field label="クロス幅(m)">
              <input
                type="number"
                step="0.01"
                value={prefs.wallpaper_width_m}
                onChange={(e) => setPrefs({ ...prefs, wallpaper_width_m: Number(e.target.value) })}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
              />
              <div className="mt-1 text-[10px] text-slate-500">関東 0.9m / 関西 0.92m が多い</div>
            </Field>
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="mt-4 w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存'}
          </button>
          {savedAt && <div className="mt-2 text-center text-[10px] text-emerald-700">保存しました {savedAt}</div>}
        </div>

        {/* 通知ポリシー(原則1) */}
        <div className="rounded-md bg-slate-50 p-3 text-[11px] text-slate-600">
          <strong>このアプリは通知しません。</strong><br />
          施工中の集中を妨げない設計です。プッシュ通知も内部通知もありません。
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}
